/** Visible dialog prose for readouts. These fragments never become action targets. */
export function collectDialogReadoutText(include: (el: HTMLElement) => boolean): string {
  try { return readVisibleDialog(include); } catch { return ''; }
}

function readVisibleDialog(include: (el: HTMLElement) => boolean): string {
  const dialogs = [...document.querySelectorAll<HTMLElement>('[role="dialog"], [role="alertdialog"], dialog[open]')]
    .filter(el => include(el));
  if (dialogs.length !== 1) return '';
  const dialog = dialogs[0];
  const walker = document.createTreeWalker(dialog, NodeFilter.SHOW_TEXT);
  const lines: string[] = [];
  let total = 0;
  let visited = 0;
  let node: Node | null;
  while ((node = walker.nextNode())) {
    if (++visited > 3000) { lines.push('[Visible text scan limit reached; readout is partial.]'); break; }
    const parent = node.parentElement;
    const text = (node.textContent || '').replace(/\s+/g, ' ').trim();
    if (!parent || !text || /^[\uE000-\uF8FF\s]+$/.test(text) ||
        isEditable(parent) ||
        parent.closest('script, style, noscript, template, input, textarea, [aria-hidden="true"], [hidden]') ||
        !include(parent)) continue;
    // Text bounds, rather than the containing panel's bounds, prevent reading
    // offscreen rows, clipped labels, or collapsed content inside a visible panel.
    const range = document.createRange();
    range.selectNodeContents(node);
    if (typeof range.getClientRects !== 'function') return '';
    const rects = [...range.getClientRects()];
    if (!rects.length || rects.some(rect => rect.width <= 0 || rect.height <= 0 ||
        rect.left < 0 || rect.top < 0 || rect.right > window.innerWidth || rect.bottom > window.innerHeight)) continue;
    let clipped = false;
    for (let ancestor: HTMLElement | null = parent; ancestor; ancestor = ancestor.parentElement) {
      const style = getComputedStyle(ancestor);
      if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') { clipped = true; break; }
      const clipX = /hidden|clip|scroll|auto/.test(style.overflowX);
      const clipY = /hidden|clip|scroll|auto/.test(style.overflowY);
      if (clipX || clipY) {
        const box = ancestor.getBoundingClientRect();
        if (rects.some(rect => (clipX && (rect.left < box.left || rect.right > box.right)) ||
            (clipY && (rect.top < box.top || rect.bottom > box.bottom)))) { clipped = true; break; }
      }
      if (ancestor === dialog) break;
    }
    if (clipped) continue;
    if (text.length > 1000) { lines.push('[Long visible text fragment omitted.]'); continue; }
    if (lines.length >= 240 || total + text.length > 12000) { lines.push('[Visible text limit reached; readout is partial.]'); break; }
    lines.push(text);
    total += text.length + 1;
  }
  return lines.join('\n');
}

function isEditable(element: HTMLElement): boolean {
  if (typeof element.isContentEditable === 'boolean') return element.isContentEditable;
  // DOM implementations without isContentEditable still inherit the nearest
  // valid enumerated contenteditable value, including an explicit false island.
  for (let parent: HTMLElement | null = element; parent; parent = parent.parentElement) {
    const value = parent.getAttribute('contenteditable')?.toLowerCase();
    if (value === 'false') return false;
    if (value === '' || value === 'true' || value === 'plaintext-only') return true;
  }
  return false;
}
